/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import { createContext, useEffect, useState, useCallback } from 'react';
import { useContext } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import { API_URL, USER_ROLES } from '../constant/constant';
import { registerPushToken } from '../services/pushNotificationService';

const UserContext = createContext({});

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const refreshUser = useCallback(
    async (identifier, force = false) => {
      try {
        if (!force && user?.id) {
          return;
        }

        const sessionToken = localStorage.getItem('sessionToken');
        if (!sessionToken) {
          setIsInitialized(true);
          return;
        }

        const storedLoginMethod = localStorage.getItem('loginMethod') || 'phone';
        const storedEmail = localStorage.getItem('emailAddress');

        let url;
        if (storedLoginMethod === 'email' && storedEmail) {
          url = `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/email?emailId=${encodeURIComponent(storedEmail)}`;
        } else {
          const mobile = identifier?.startsWith('+91') ? identifier?.replace('+91', '') : identifier;
          url = `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor?mobileNo=%2B91${mobile}`;
        }

        setUserLoading(true);
        const { data } = await axiosInstance.get(url, {
          headers: {
            sessionToken,
            'X-USER-ROLE': USER_ROLES.VENDOR,
          },
        });

        if (data?.id) {
          setUser(data);

          // Store vendor ID for later use (e.g., logout)
          localStorage.setItem('vendorId', data?.id);

          // Re-register FCM token on app launch (fire-and-forget)
          registerPushToken(sessionToken, USER_ROLES.VENDOR, data?.id).catch((err) => {
            console.log('Push notification re-registration skipped:', err.message);
          });
        } else {
          logout();
        }
      } catch (error) {
        // If error is 403 or 401, logout
        if (error?.response?.status === 401) {
          logout();
        }
      } finally {
        setUserLoading(false);
        setIsInitialized(true);
      }
    },
    [user?.id, logout]
  );

  const forceRefreshUser = useCallback(
    async (identifier) => {
      try {
        const sessionToken = localStorage.getItem('sessionToken');
        if (!sessionToken) {
          logout();
          return;
        }

        const storedLoginMethod = localStorage.getItem('loginMethod') || 'phone';
        const storedEmail = localStorage.getItem('emailAddress');

        let url;
        if (storedLoginMethod === 'email' && storedEmail) {
          url = `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/email?emailId=${encodeURIComponent(storedEmail)}`;
        } else {
          const mobile = identifier?.startsWith('+91') ? identifier?.replace('+91', '') : identifier;
          url = `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor?mobileNo=%2B91${mobile}`;
        }

        setUserLoading(true);
        const { data } = await axiosInstance.get(url, {
          headers: {
            sessionToken,
            'X-USER-ROLE': USER_ROLES.VENDOR,
          },
        });

        if (data?.id) {
          setUser(data);

          // Store vendor ID for later use
          localStorage.setItem('vendorId', data?.id);

          // Re-register FCM token (fire-and-forget)
          registerPushToken(sessionToken, USER_ROLES.VENDOR, data?.id).catch((err) => {
            console.log('Push notification re-registration skipped:', err.message);
          });
        } else {
          console.log('You have to Login first!');
          logout();
        }
      } catch (error) {
        console.error('Error force refreshing user:', error);
        if (error?.response?.status === 403 || error?.response?.status === 401) {
          logout();
        }
      } finally {
        setUserLoading(false);
      }
    },
    [logout]
  );

  const clearUser = useCallback(() => {
    setUser(null);
    setIsInitialized(false);
  }, []);

  useEffect(() => {
    // Listen for logout events from AuthContext
    const handleLogout = () => {
      clearUser();
    };

    // Listen for login events from AuthContext
    const handleLogin = (event) => {
      const { phone } = event.detail;
      if (phone) {
        refreshUser(phone, true); // Force refresh
      }
    };

    window.addEventListener('user-logout', handleLogout);
    window.addEventListener('user-login', handleLogin);

    return () => {
      window.removeEventListener('user-logout', handleLogout);
      window.removeEventListener('user-login', handleLogin);
    };
  }, [clearUser, refreshUser]);

  useEffect(() => {
    const sessionToken = localStorage.getItem('sessionToken');
    const contactNo = localStorage.getItem('contactNo');
    const storedLoginMethod = localStorage.getItem('loginMethod') || 'phone';
    const storedEmail = localStorage.getItem('emailAddress');

    if (!sessionToken) {
      setIsInitialized(true);
      return;
    }

    // For email login, we don't need contactNo
    if (!contactNo && !(storedLoginMethod === 'email' && storedEmail)) {
      logout();
      return;
    }

    // Only fetch if not already initialized
    if (!isInitialized) {
      const identifier = storedLoginMethod === 'email' && storedEmail ? storedEmail : contactNo;
      refreshUser(identifier);
    }
  }, []);

  const value = {
    setUser,
    user,
    forceRefreshUser,
    userLoading,
    setUserLoading,
    refreshUser,
    isInitialized,
    clearUser,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
